import { Component, ElementRef, ViewChild, effect } from "@angular/core";
import { Router } from "@angular/router";
import { INotification } from "../../interfaces";
import NotificationV2Service from "../services/notification-v2.service";
import { ArcusLayoutService } from "../services/arcus-layout.service";
import { MatToolbar } from "@angular/material/toolbar";
import { MatIconButton, MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { NotificationRowComponent } from "./notification-row/notification-row.component";

@Component({
    selector: "notification",
    templateUrl: "./notification.component.html",
    styleUrls: ["./notification.component.scss"],
    imports: [MatToolbar, MatIconButton, MatIcon, MatButton, NotificationRowComponent],
})
export class NotificationComponent {
    private static readonly PAGE_SIZE = 8;

    // Matches the mat-sidenav slide-in transition so the scroll starts once the panel has settled,
    // rather than racing its transform and dragging the whole page sideways mid-animation.
    private static readonly SIDENAV_TRANSITION_MS = 400;

    @ViewChild("notificationsList") notificationsList?: ElementRef<HTMLElement>;
    @ViewChild("attentionSection") attentionSection?: ElementRef<HTMLElement>;

    visibleCount = NotificationComponent.PAGE_SIZE;

    constructor(
        public layoutService: ArcusLayoutService,
        private notificationService: NotificationV2Service,
        public router: Router
    ) {
        effect(() => {
            this.notificationService.notifications();
            $("#notification-toggle").addClass("ring").removeClass("ring");
        });

        effect(() => {
            if (this.layoutService.scrollToAttentionRequest() === 0) return;
            setTimeout(() => {
                const container = this.notificationsList?.nativeElement;
                const section = this.attentionSection?.nativeElement;
                if (container && section) {
                    const offset = section.getBoundingClientRect().top - container.getBoundingClientRect().top;
                    container.scrollTo({ top: container.scrollTop + offset, behavior: "smooth" });
                }
            }, NotificationComponent.SIDENAV_TRANSITION_MS);
        });
    }

    get activeNotifications(): INotification[] {
        return this.notificationService.notifications().filter((n) => !n.IsDismissed && !this.isExpired(n));
    }

    get attention(): INotification[] {
        return this.activeNotifications
            .filter((n) => n.Type === "alert")
            .sort((a, b) => (this.isFraudAlert(b) ? 1 : 0) - (this.isFraudAlert(a) ? 1 : 0) || b.ID - a.ID);
    }

    get others(): INotification[] {
        return this.activeNotifications.filter((n) => n.Type !== "alert").sort((a, b) => b.ID - a.ID);
    }

    get shownOthers(): INotification[] {
        return this.others.slice(0, this.visibleCount);
    }

    get remainingCount(): number {
        return Math.min(NotificationComponent.PAGE_SIZE, this.others.length - this.visibleCount);
    }

    showMore(): void {
        this.visibleCount += NotificationComponent.PAGE_SIZE;
    }

    notificationToggle(notification?: INotification) {
        if (!notification) return;
        this.notificationService.dismissNotification(notification);
        const route = notification.Action?.Route?.replace(/^~/, "").toLowerCase();
        if (!route) return;
        this.router.navigate([route], { queryParams: notification.Action?.QueryParams ?? undefined });
    }

    dismissNotification(notification: INotification) {
        this.notificationService.dismissNotification(notification);
    }

    dismissAll(): void {
        this.notificationService.dismissAllNotifications();
    }

    isExpired(notification: INotification): boolean {
        if (!notification.ExpireDateTime) return false;
        return new Date(notification.ExpireDateTime).getTime() < Date.now();
    }

    isFraudAlert(notification: INotification): boolean {
        return notification.Metadata?.[NotificationV2Service.FRAUD_METADATA_KEY] === "true";
    }
}
